import { forwardRef, useRef, useImperativeHandle } from 'react'
import Dropzone, { DropzoneRef, FileRejection } from 'react-dropzone'

import { Box } from '@corabank/arco'

import { EmptyFileInput, FilledFile } from './components'
import useHelpers from './hooks/use-helpers'
import { FileUploadProps, WrapperChildrenParams } from './interface'
import { isLargeFile } from './utils'

const FileUpload = forwardRef<DropzoneRef | undefined, FileUploadProps>(
  (
    {
      id,
      name,
      className,
      onRemoveFile = () => undefined,
      onChange,
      multiple,
      maxFiles = 1,
      onMaxFiles,
      onMaxSize,
      onReject,
      ...props
    },
    ref
  ) => {
    const fileUploadInput = useRef(null)
    const dropzoneRef = useRef(null)

    const { files, removeFile, handleDrop } = useHelpers({
      onChange,
      onDrop: props.onDrop,
      maxFiles,
      onMaxFiles,
    })

    useImperativeHandle(ref, () => ({
      // @ts-ignore
      open: () => dropzoneRef.current.open(),
      // @ts-ignore
      removeFile: () => fileUploadInput.current.click(),
    }))

    const handleRejectFiles = (files: FileRejection[]): void => {
      if (files.length > maxFiles && onMaxFiles) return onMaxFiles()
      if (isLargeFile(files) && onMaxSize) return onMaxSize()

      return onReject && onReject()
    }

    const renderSingleFile = ({
      isDragAccept,
      getRootProps,
      getInputProps,
    }: WrapperChildrenParams): JSX.Element => (
      <>
        <Box
          data-testid="fileupload-single"
          {...getRootProps()}
          className={className}
        >
          {files?.length === 0 && (
            <EmptyFileInput
              isDragAccept={isDragAccept}
              id={id}
              name={name}
              getInputProps={getInputProps}
            />
          )}
        </Box>
        {files?.length !== 0 && (
          <FilledFile
            key="file-accepted-0"
            file={files?.[0]}
            removeFile={removeFile(0)}
            onRemoveFile={onRemoveFile}
            ref={fileUploadInput}
          />
        )}
      </>
    )

    const renderMultipleFiles = ({
      isDragAccept,
      getRootProps,
      getInputProps,
    }: WrapperChildrenParams): JSX.Element => (
      <>
        <Box data-testid="fileupload-multiple" {...getRootProps()}>
          {files?.length !== maxFiles && (
            <EmptyFileInput
              isDragAccept={isDragAccept}
              id={id}
              name={name}
              getInputProps={getInputProps}
            />
          )}
        </Box>

        {files?.map((file, index) => (
          <FilledFile
            key={`file-accepted-${index}`}
            file={file}
            removeFile={removeFile(index)}
            onRemoveFile={onRemoveFile}
          />
        ))}
      </>
    )

    return (
      <Dropzone
        {...props}
        onDrop={handleDrop}
        multiple={multiple}
        maxFiles={maxFiles}
        ref={dropzoneRef}
        onDropRejected={handleRejectFiles}
      >
        {multiple ? renderMultipleFiles : renderSingleFile}
      </Dropzone>
    )
  }
)

FileUpload.displayName = 'FileUpload'

export { FileUpload }
